/**
 * Production URLs for various application domains.
 */
const prodAppDomains = {
  StoreUS: `https://www.yohana.com`,
  StoreJP: 'https://www.yohana.jp',
  PoplarUS: 'https://app.yohana.com',
  PoplarJP: 'https://app.yohana.jp',
};

/**
 * Type representing the structure of application domains.
 */
type AppDomainsType = typeof prodAppDomains;

/**
 * Type representing application domains with optional environment URLs.
 */
export type AppDomains = {
  [K in keyof AppDomainsType]?: string;
};

/**
 * Type for mapping production URLs to environment URLs.
 */
export type ReversedAppDomains = {
  [K in AppDomainsType[keyof AppDomainsType]]: string;
};

/**
 * An array containing keys of the AppDomainsType.
 */
const keysOfAppDomains: (keyof AppDomainsType)[] = Object.keys(
  prodAppDomains,
) as (keyof AppDomainsType)[];

/**
 * Retrieves a mapping of production domain URLs to environment domain URLs.
 * @param {string} env - The current environment.
 * @param {AppDomains} envDomains - The environment-specific domain URLs.
 * @returns {ReversedAppDomains | null} The mapping of production domain URLs to environment domain URLs, or null if no mapping is needed.
 */
export const getPrismicDomainMapping = (
  env: string,
  envDomains: AppDomains,
): ReversedAppDomains | null => {
  // If the environment is production, no mapping needed
  if (env === 'prod') return null;

  /**
   * The mapping of production domain URLs to environment domain URLs.
   */
  const domainMapping: ReversedAppDomains = {};

  for (const key of keysOfAppDomains) {
    const envUrl = envDomains[key];
    const prodUrl = prodAppDomains[key];

    // Check if an environment-specific URL exists and is different from the production URL
    if (envUrl && prodUrl !== envUrl) {
      domainMapping[prodUrl] = envUrl; // Create a mapping for content replacement
    }
  }

  // Return the mapping if it contains items, otherwise return null
  return Object.keys(domainMapping).length > 0 ? domainMapping : null;
};

/**
 * The cached domain mapping.
 */
let cachedDomainMapping: ReversedAppDomains | null = null;

/**
 * Indicates whether the domain mapping is set.
 */
let isDomainMappingSet = false;

/**
 * Caches the domain mapping if not already set.
 * @param {string} env - The current environment.
 * @param {AppDomains} envDomains - The environment-specific domain URLs.
 */
const setCachedPrismicDomainMapping = (env: string, envDomains: AppDomains): void => {
  // Cache the domain mapping if not already set
  if (!isDomainMappingSet) {
    cachedDomainMapping = getPrismicDomainMapping(env, envDomains);
    isDomainMappingSet = true;
  }
};

/**
 * Retrieves the cached domain mapping or sets it if not available.
 * @param {string} env - The current environment.
 * @param {AppDomains} envDomains - The environment-specific domain URLs.
 * @returns {AppDomains} The cached domain mapping.
 */
const getCachedPrismicDomainMapping = (env: string, envDomains: AppDomains): ReversedAppDomains => {
  // Get cached domain mapping or set it if not available
  if (!isDomainMappingSet) {
    setCachedPrismicDomainMapping(env, envDomains);
  }
  return cachedDomainMapping!;
};

export { getCachedPrismicDomainMapping };
